import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { AiFillApple } from 'react-icons/ai';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/i18n';
import { useDownload } from '@/hooks/useDownload';
import { detectPlatform, getLatestVersion } from '@/api/version';
import {
  AGENTIC_APPS, CHAT_APPS_COUNT, CONTACT_EMAIL, FILM_DURATION, FILM_POSTER, PRESS, TESTIMONIALS, YOUTUBE_ID,
} from './data';
import { Icon, PlayIcon, ScreenView } from './parts';
import { Lines, fill, useLanding } from './i18n';
import ContactEmail from './ContactEmail';

const YT_EMBED = `https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
const YT_WATCH = `https://www.youtube.com/watch?v=${YOUTUBE_ID}`;

function YouTubeFrame() {
  return (
    <iframe
      src={YT_EMBED}
      title="Estha for Mac film"
      allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
    />
  );
}

/* ---------------- Press ---------------- */
// Stats used to live here too; they now sit where they belong (50+ on the Chat Apps card,
// 6 on the Agentic Apps card, 0 bytes on the hero shot, 12 languages in the FAQ).
export function Stats() {
  const { t } = useLanding();
  if (PRESS.length === 0) return null;
  return (
    <section className="proof">
      <div className="wrap press r">
        <span>{t.proof.press}</span>
        {PRESS.map((p) => (
          <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer">
            {p.logo ? <img src={`/landing/${p.logo}`} alt={p.name} /> : <b>{p.name}</b>}
          </a>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Film ---------------- */
export function Film() {
  const { t } = useLanding();
  const [playing, setPlaying] = useState(false);
  return (
    <section className="pad-s" id="film">
      <div className="wrap">
        <span className="kicker r">{t.film.kicker}</span>
        <h2 className="h2 r"><Lines text={t.film.title} /></h2>
        {playing ? (
          <div className="film"><YouTubeFrame /></div>
        ) : (
          <button className="film r" onClick={() => setPlaying(true)} aria-label="Play the Estha for Mac film">
            {FILM_POSTER ? (
              <img src={`/landing/${FILM_POSTER}`} alt="Estha for Mac film" loading="lazy" />
            ) : (
              // A calm branded cover instead of a dense app screenshot (the gallery below shows the app).
              <span className="film-cover" aria-hidden>
                <img src="/landing/estha-icon-128.png" alt="" />
                <b>Estha for Mac</b>
                <em>{fill(t.hero.titlePre + t.hero.titleMark + t.hero.titlePost).replace(/\n/g, ' ')}</em>
              </span>
            )}
            <span className="play"><PlayIcon /></span>
            <span className="flabel">{t.hero.watch}{FILM_DURATION && ` · ${FILM_DURATION}`}</span>
          </button>
        )}
        <p style={{ marginTop: 14, fontSize: 14 }}>
          <a className="link-arr" href={YT_WATCH} target="_blank" rel="noopener noreferrer">
            {t.film.watchOnYouTube}<span>↗</span>
          </a>
        </p>
      </div>
    </section>
  );
}

export function FilmLightbox({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useLanding();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="lb on" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="box"><YouTubeFrame /></div>
      <button className="x" onClick={onClose} aria-label="Close">×</button>
      <a className="ytlink" href={YT_WATCH} target="_blank" rel="noopener noreferrer">
        {t.film.trouble}
      </a>
    </div>
  );
}

/* ---------------- Overview ---------------- */
export function Overview() {
  const { t } = useLanding();
  const o = t.overview;
  const cards = [
    { n: '01', href: '#chat', icon: 'chat', ...o.chat, free: true },
    { n: '02', href: '#build', icon: 'plus', ...o.build, free: false },
    { n: '03', href: '#agentic', icon: 'grid', ...o.agentic, free: false },
    { n: '04', href: '#org', icon: 'building', ...o.org, free: false },
  ];
  return (
    <section className="pad-s" id="overview">
      <div className="wrap">
        <span className="kicker r">{o.kicker}</span>
        <h2 className="h2 r"><Lines text={o.title} /></h2>
        <div className="plat">
          {cards.map((c) => (
            <a key={c.n} className="pcard r" href={c.href}>
              <span className="pn">{c.n}</span>
              <i><Icon name={c.icon} width={1.8} /></i>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
              <span className={`tagx${c.free ? ' free' : ''}`}>{c.n === '01' ? `${CHAT_APPS_COUNT}+ Chat Apps · ` : ''}{fill(c.tag)}</span>
              <span className="go">{o.explore} →</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- 01 Chat Apps ---------------- */
export function ChatApps() {
  const { t } = useLanding();
  const c = t.chat;
  const feats = [
    ['book', c.feats.kb],
    ['clip', c.feats.files],
    ['search', c.feats.browse],
  ] as const;
  return (
    <section className="pad" id="chat" style={{ background: 'var(--paper-2)' }}>
      <div className="wrap split">
        <div className="txt">
          <span className="kicker r">{c.kicker}</span>
          <h2 className="h2 r">
            <Lines text={c.title} />
            <span className="freepill">{c.freePill}</span>
          </h2>
          <p className="lede r">{c.lede}</p>
          <ul className="feat r">
            {feats.map(([icon, f]) => (
              <li key={icon}><i><Icon name={icon} width={2} /></i><div><b>{f.title}</b><span className="d">{f.desc}</span></div></li>
            ))}
          </ul>
        </div>
        <ScreenView id="chat" className="r" />
      </div>
    </section>
  );
}

/* ---------------- 02 Agentic Apps ---------------- */
export function AgenticApps() {
  const { t } = useLanding();
  const [tab, setTab] = useState(0);
  return (
    <section className="pad" id="agentic" style={{ background: 'var(--paper-2)' }}>
      <div className="wrap">
        <span className="kicker r">{t.agentic.kicker}</span>
        <h2 className="h2 r"><Lines text={t.agentic.title} /></h2>
        <p className="lede r">{t.agentic.lede}</p>

        {/* All of them at a glance; picking one opens it in the gallery below. */}
        <div className="acards">
          {AGENTIC_APPS.map((a, i) => (
            <button
              key={a.key}
              className="acard r"
              aria-current={i === tab}
              onClick={() => {
                setTab(i);
                document.getElementById('agentic-gallery')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              <i style={{ background: a.color }}><Icon name={a.icon} width={2} /></i>
              <h3>{t.apps[a.key].name}</h3>
              <p>{t.screens[a.screen][t.screens[a.screen].length - 1]?.caption}</p>
              <span className="gtags">{t.apps[a.key].tags.slice(0, 2).map((tag) => <span key={tag}>{tag}</span>)}</span>
            </button>
          ))}
        </div>

        <div className="gal" id="agentic-gallery">
          <div className="gal-h r">
            <h3>{t.agentic.seeThem}</h3>
            <div className="gtabs">
              {AGENTIC_APPS.map((a, i) => (
                <button key={a.key} className="gtab" aria-selected={i === tab} onClick={() => setTab(i)}>
                  <i style={{ background: a.color }} />{t.apps[a.key].name}
                </button>
              ))}
            </div>
          </div>
          {AGENTIC_APPS.map((a, i) => i === tab && (
            <div key={a.key} className="gpanel on">
              <div>
                <p className="q">{t.apps[a.key].name}</p>
                <p>{t.apps[a.key].description}</p>
                <div className="gtags">{t.apps[a.key].tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
              </div>
              <ScreenView id={a.screen} className="lp-scrub" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- 03 Build ---------------- */
export function Build() {
  const { t } = useLanding();
  const b = t.build;
  return (
    <section className="pad" id="build">
      <div className="wrap">
        <span className="kicker r">{b.kicker}</span>
        <h2 className="h2 r"><Lines text={b.title} /></h2>
        <p className="lede r">{b.lede}</p>
        <div className="build-reel r"><ScreenView id="build" /></div>
        <div className="picker">
          <div className="pk r">
            <div className="pk-h"><h3>Quick Bot</h3><small className="rec">{b.quick.badge}</small></div>
            <p>{b.quick.desc}</p>
            <ul>{b.quick.items.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
          <div className="pk r">
            <div className="pk-h"><h3>{b.advanced.title}</h3><small className="pro">{t.pricing.premium.name}</small></div>
            <p>{b.advanced.desc}</p>
            <ul>{b.advanced.items.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        </div>
        <div className="guide r">
          <i><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M12 0c.6 5.6 3.4 9.4 12 12-8.6 2.6-11.4 6.4-12 12-.6-5.6-3.4-9.4-12-12 8.6-2.6 11.4-6.4 12-12z" /></svg></i>
          <p>
            <b>{b.guideTitle}</b>{' '}
            {b.guideText}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Private by design ---------------- */
export function Private() {
  const { t } = useLanding();
  const p = t.private;
  return (
    <section className="night pad" id="private">
      <div className="wrap" style={{ position: 'relative' }}>
        <span className="kicker r">{p.kicker}</span>
        <h2 className="h2 r"><Lines text={p.title} /></h2>
        <p className="lede r">{p.lede}</p>
        {/* A left-to-right picture of where work can run, not a picker: the dashed frame is the data boundary. */}
        <div className="where r">
          <span className="boundary">{p.boundary}</span>
          {([
            ['monitor', p.local.title, p.local.desc, p.local.best],
            ['server', p.server.title, p.server.desc, p.server.best],
            ['grid4', 'Mac Studio Cluster', p.cluster.desc, p.cluster.best],
          ] as const).map(([icon, title, desc, best], i) => (
            <div key={icon} className="wcard">
              {i > 0 && <span className="arrow" aria-hidden>→</span>}
              <i><Icon name={icon} width={2} /></i>
              <h4>{title}</h4>
              <p>{desc}</p>
              <small>{best}</small>
            </div>
          ))}
        </div>
        <div className="models r">
          <div>
            <b>16GB+</b><h4>{p.models.every}</h4>
            <ul><li>Qwen · Gemma</li><li>{p.models.whisper}</li></ul>
          </div>
          <div>
            <b>32GB+</b><h4>{p.models.recommended}</h4>
            <ul><li>{p.models.larger}</li><li>{p.models.vision}</li></ul>
          </div>
          <div>
            <b>MLX</b><h4>{p.models.open}</h4>
            <ul>{p.models.openItems.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- 04 My Org ---------------- */
export function Org() {
  const { t } = useLanding();
  const o = t.org;
  const feats = [
    ['shieldCheck', o.feats.review, 'My Org Admin'],
    ['users', o.feats.access, 'My Org Admin'],
    ['image', o.feats.brand, 'Estha for Mac'],
  ] as const;
  return (
    <section className="pad" id="org">
      <div className="wrap">
        <div className="split rev">
          <div className="txt">
            <span className="kicker r">{o.kicker}</span>
            <h2 className="h2 r"><Lines text={o.title} /></h2>
            <p className="lede r">{o.lede}</p>
            <div className="org-cta r">
              {/* My Org is set up with the Estha team first, so contact is the only action here. */}
              <ContactEmail className="btn-ink">{o.talk} →</ContactEmail>
            </div>
          </div>
          <ScreenView id="org" className="r" />
        </div>
        <div className="org-feats">
          {feats.map(([icon, f, where]) => (
            <div key={icon} className="of r">
              <i><Icon name={icon} width={2} /></i>
              <h4>{f.title}</h4>
              <p>{f.desc}</p>
              <span className="where-tag">{where}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Pricing ---------------- */
export function Pricing() {
  const { language } = useI18n();
  const { t } = useLanding();
  const p = t.pricing;
  const { isLoading, downloadLatest } = useDownload();
  return (
    <section className="pad" id="pricing">
      <div className="wrap">
        {TESTIMONIALS.length > 0 && (
          <div className="voices">
            <span className="kicker r">{t.proof.voices}</span>
            <div className="vgrid">
              {TESTIMONIALS.map((v) => (
                <figure key={v.name} className="r">
                  <blockquote>“{v.quote}”</blockquote>
                  <figcaption><b>{v.name}</b> · {v.role} · {v.industry}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}
        <span className="kicker r">{p.kicker}</span>
        <h2 className="h2 r"><Lines text={p.title} /></h2>
        <div className="prices">
          <div className="pr r">
            <h3>{p.free.name}</h3>
            <div className="price">$0</div>
            <p className="sub">{p.free.sub}</p>
            <ul>{p.free.items.map((x) => <li key={x}>{x}</li>)}</ul>
            <div className="act">
              <span className="lp-dl-wrap">
                <Button
                  size="lg"
                  className="text-base px-8 bg-[#1E88C8] hover:bg-[#1670A8] text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                  onClick={downloadLatest}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" />
                      {language === 'en' ? 'Loading...' : '加载中...'}
                    </>
                  ) : (
                    <>
                      <AiFillApple className="mr-1" />
                      {language === 'en' ? 'Download Estha for Mac' : '下载 Estha for Mac'}
                    </>
                  )}
                </Button>
              </span>
            </div>
          </div>
          <div className="pr hl r" data-flag={p.premium.flag}>
            <h3>{p.premium.name}</h3>
            <div className="price">$100<small>{p.premium.firstYear}</small></div>
            <p className="sub">{p.premium.sub}</p>
            <ul>{p.premium.items.map((x) => <li key={x}>{x}</li>)}</ul>
            <div className="act"><a className="dark" href="#top">{p.premium.cta}</a></div>
          </div>
          <div className="pr r">
            <h3>{p.enterprise.name}</h3>
            <div className="price" style={{ fontSize: 30, padding: '5px 0' }}>{p.enterprise.price}</div>
            <p className="sub">{p.enterprise.sub}</p>
            <ul>{p.enterprise.items.map((x) => <li key={x}>{x}</li>)}</ul>
            <div className="act"><ContactEmail block align="center">{CONTACT_EMAIL}</ContactEmail></div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
/** Latest app version from the same API the download buttons use (read-only). */
function useLatestVersionLabel() {
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    getLatestVersion(detectPlatform())
      .then((v) => { if (live && v?.version) setVersion(v.version); })
      .catch(() => { /* row stays hidden if the API can't be reached */ });
    return () => { live = false; };
  }, []);
  return version;
}

export function Faq() {
  const { t } = useLanding();
  const f = t.faq;
  const version = useLatestVersionLabel();
  return (
    <section className="pad" id="faq" style={{ background: 'var(--paper-2)' }}>
      <div className="wrap faq-grid">
        <div>
          <span className="kicker r">FAQ</span>
          <h2 className="h2 r"><Lines text={f.title} /></h2>
          <dl className="spec r">
            <div><dt>{f.spec.chip}</dt><dd>Apple Silicon</dd></div>
            <div><dt>{f.spec.memory}</dt><dd>{f.spec.memMin}<small>{f.spec.memRec}</small></dd></div>
            <div><dt>{f.spec.languages}</dt><dd>{f.spec.langsVal}</dd></div>
            {version && <div><dt>{f.spec.version}</dt><dd>{version}</dd></div>}
          </dl>
        </div>
        <div className="r">
          {f.qa.map(({ q, a }, i) => (
            <details key={i} open={i === 0}>
              <summary><span>{q}</span><span className="pm"><Icon name="plus" width={2.4} /></span></summary>
              <p>{fill(a)}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
